import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Root error boundary.
 *
 * Without one, a single render error blanks the entire application to a white
 * screen — on a record-room counter that looks exactly like the system being
 * down, and the clerk has nothing to report but "it vanished".
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Goes to the browser console today. When the department provides an error
    // collector, this is the one place to send it.
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4">
        <div className="panel w-full max-w-md px-6 py-7 text-center">
          <h1 className="text-lg font-semibold">Something went wrong</h1>
          <p className="mt-2 text-ink-500">
            The screen could not be displayed. Your data has not been changed.
          </p>
          <p className="mt-1 text-ink-500">कुछ गड़बड़ हुई। आपका डेटा सुरक्षित है।</p>

          <div className="mt-5 flex justify-center gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => this.setState({ error: null })}
            >
              Try again
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Go to dashboard
            </button>
          </div>

          <details className="mt-5 text-left">
            <summary className="cursor-pointer text-[0.8125rem] text-ink-500">
              Technical detail
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto rounded border border-line bg-paper p-2 text-[0.75rem] text-ink-700">
              {error.message}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
